import React, { useState } from "react";
import SellerAuth from ".";
import { AUTH_FORM_SHELL_CLASS } from "./auth-shell";
import Header from "../components/header";
import { Grid2, LinearProgress } from "@mui/material";
import MaInput from "../../Components/UI/ma-input";
import MaButton from "../../Components/UI/ma-button";
import { AnimatePresence, motion } from "framer-motion";
import { useFormik } from "formik";
import * as yup from "yup";
import { IoArrowBack } from "react-icons/io5";
import { IoIosArrowForward } from "react-icons/io";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { CREATE_STORE } from "../../Utils/Mutations";
import { useProfile } from "../../Utils/ProfileContext";
import GoogleAddressAutocomplete from "../../Components/Common/google-address-autocomplete";

const STORE_STEP = 1;
const ADDRESS_STEP = 2;

const STEPS = [
  {
    id: STORE_STEP,
    label: "Store details",
    heading: "Create your store",
    subheading: "Tell us about the business you'll be selling under.",
    progress: 50,
  },
  {
    id: ADDRESS_STEP,
    label: "Business address",
    heading: "Where is your business based?",
    subheading: "Help buyers know where they're shopping from.",
    progress: 100,
  },
];

const SCHEMAS = {
  [STORE_STEP]: yup.object({
    storeName: yup
      .string()
      .trim()
      .required("Store name is required")
      .min(2, "Too short"),
    registeredNumber: yup.string().nullable(),
  }),
  [ADDRESS_STEP]: yup.object({
    employerIdNumber: yup.string().nullable(),
    address: yup
      .string()
      .trim()
      .required("Address is required")
      .min(5, "Enter a valid address"),
  }),
};

const STEP_FIELDS = {
  [STORE_STEP]: ["storeName", "registeredNumber"],
  [ADDRESS_STEP]: ["employerIdNumber", "address"],
};

const EASE = [0.22, 1, 0.36, 1];

const stepVariants = {
  enter: (direction) => ({
    opacity: 0,
    x: direction > 0 ? 32 : -32,
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.45,
      ease: EASE,
      when: "beforeChildren",
      staggerChildren: 0.07,
    },
  },
  exit: (direction) => ({
    opacity: 0,
    x: direction > 0 ? -32 : 32,
    transition: { duration: 0.3, ease: EASE },
  }),
};

const fieldVariants = {
  enter: { opacity: 0, y: 14 },
  center: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: EASE },
  },
};

const successVariants = {
  hidden: { opacity: 0, y: 16, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: EASE,
      when: "beforeChildren",
      staggerChildren: 0.12,
    },
  },
};

const successItem = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
};

const CreateSellerStore = () => {
  const navigate = useNavigate();
  const { refetchProfile } = useProfile();

  const [step, setStep] = useState(STORE_STEP);
  const [direction, setDirection] = useState(1);
  const [storeCreated, setStoreCreated] = useState(false);
  
  const default_plan = "6a171bddb881ffd77fb48118"

  const [createStore, { loading: createLoading, error: createError }] =
    useMutation(CREATE_STORE);

  const formik = useFormik({
    initialValues: {
      storeName: "",
      registeredNumber: "",
      employerIdNumber: "",
      address: "",
    },
    validateOnChange: true,
    validateOnBlur: true,
    validate: (values) => {
      const errors = {};
      const schema = SCHEMAS[step];
      if (!schema) return errors;
      try {
        schema.validateSync(values, { abortEarly: false });
      } catch (err) {
        if (err.inner) {
          err.inner.forEach((e) => {
            if (e.path && !errors[e.path]) errors[e.path] = e.message;
          });
        }
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        await createStore({
          variables: {
            storeName: values.storeName.trim(),
            registeredNumber: values.registeredNumber?.trim() || null,
            employerIdentificationNumber:
              values.employerIdNumber?.trim() || null,
            address: values.address.trim(),
            storeLogo: null,
            storeDescription: null,
            categoriesToSell: [],
            planId: default_plan,
          },
        });
        try {
          await refetchProfile();
        } catch (e) {
          // Non-fatal: success screen still shows.
        }
        setStoreCreated(true);
      } catch (err) {
        // createError covers UI state.
      }
    },
  });

  const currentStep = STEPS.find((s) => s.id === step) || STEPS[0];

  const showError = (field) =>
    Boolean(formik.touched[field] && formik.errors[field]);

  const helper = (field) => (showError(field) ? formik.errors[field] : "");

  const validateStepFields = async (fields) => {
    const touchedObj = {};
    fields.forEach((f) => {
      touchedObj[f] = true;
    });
    formik.setTouched({ ...formik.touched, ...touchedObj }, false);
    const errors = await formik.validateForm();
    return fields.every((f) => !errors[f]);
  };

  const validateAndProceed = async () => {
    const ok = await validateStepFields(STEP_FIELDS[step]);
    if (!ok) return;

    if (step < STEPS.length) {
      setDirection(1);
      setStep(step + 1);
    } else {
      formik.handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(step - 1);
    }
  };

  const isFinalStep = step === STEPS.length;
  const ctaTitle = isFinalStep ? "Create store" : "Continue";

  if (storeCreated) {
    return (
      <div>
        <Header />
        <div className="flex min-h-[100dvh] items-center justify-center px-4 pt-[80px] sm:px-6">
          <motion.div
            variants={successVariants}
            initial="hidden"
            animate="visible"
            className="text-center max-w-[440px] w-full"
          >
            <motion.div
              variants={successItem}
              className="text-[44px] sm:text-[52px] leading-none"
              role="img"
              aria-label="celebration"
            >
              🎉
            </motion.div>
            <motion.h2
              variants={successItem}
              className="my-0 mt-4 text-[#353535] text-[20px] sm:text-[22px] font-bold"
            >
              Store is live!
            </motion.h2>
            <motion.p
              variants={successItem}
              className="mt-2 text-[13px] sm:text-[14px] text-[#5C6A7A] leading-relaxed"
            >
              Congratulations! Your Mamaket vendor store is set up and your
              first product is queued for review.
            </motion.p>
            <motion.div variants={successItem} className="mt-6 w-full [&>div]:w-full sm:[&>div]:w-auto">
              <MaButton
                onClick={() => navigate("/seller/dashboard")}
                variant="primary"
                fullWidth
                title="Go to dashboard"
                sx={{
                  bgcolor: "#2E1834",
                  borderRadius: "12px",
                  minWidth: { xs: "100%", sm: 220 },
                }}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <SellerAuth>
      <div className={AUTH_FORM_SHELL_CLASS}>
        <div>
          <span className="text-[#CC9901] font-bold text-[13px] sm:text-[14px]">
            Step {step} of {STEPS.length}: {currentStep.label}
          </span>
          <LinearProgress
            variant="determinate"
            value={currentStep.progress}
            sx={{
              borderRadius: "50px",
              mt: 1,
              height: 5,
              backgroundColor: "#F5E6B8",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#CC9901",
                borderRadius: "50px",
                transition: "transform 0.7s cubic-bezier(0.22,1,0.36,1)",
              },
            }}
          />
        </div>

        <div className="mt-6">
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <motion.div
              key={step}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <motion.h2
                variants={fieldVariants}
                className="my-0 text-[#353535] text-[22px] sm:text-[26px] md:text-[28px] font-bold"
              >
                {currentStep.heading}
              </motion.h2>
              <motion.span
                variants={fieldVariants}
                className="block mt-2 text-[13px] sm:text-[14px] text-[#222222]"
              >
                {currentStep.subheading}
              </motion.span>

              <div className="mt-6">
                <Grid2 container spacing={2}>
                  {step === STORE_STEP && (
                    <>
                      <Grid2 size={{ xs: 12 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            id="storeName"
                            name="storeName"
                            label="Business / Store name"
                            placeholder="Enter store name"
                            fullWidth
                            value={formik.values.storeName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showError("storeName")}
                            helperText={helper("storeName")}
                          />
                        </motion.div>
                      </Grid2>
                      <Grid2 size={{ xs: 12 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            id="registeredNumber"
                            name="registeredNumber"
                            label="Registered number (Optional)"
                            placeholder="Enter registration number"
                            fullWidth
                            value={formik.values.registeredNumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showError("registeredNumber")}
                            helperText={helper("registeredNumber")}
                          />
                        </motion.div>
                      </Grid2>
                    </>
                  )}

                  {step === ADDRESS_STEP && (
                    <>
                      <Grid2 size={{ xs: 12 }}>
                        <motion.div variants={fieldVariants}>
                          <MaInput
                            id="employerIdNumber"
                            name="employerIdNumber"
                            label="Employer Identification Number (Optional)"
                            placeholder="Enter EIN"
                            fullWidth
                            value={formik.values.employerIdNumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={showError("employerIdNumber")}
                            helperText={helper("employerIdNumber")}
                          />
                        </motion.div>
                      </Grid2>
                      <Grid2 size={{ xs: 12 }}>
                        <motion.div variants={fieldVariants}>
                          <GoogleAddressAutocomplete
                            label="Business address"
                            placeholder="Street, city, state, country"
                            value={formik.values.address}
                            borderColor={showError("address") ? "#FF465C" : "#E5E7EB"}
                            onAddressChange={({ address }) => {
                              formik.setFieldValue("address", address);
                              formik.setFieldTouched("address", true, false);
                            }}
                          />
                          {showError("address") ? (
                            <p className="my-0 mt-1 text-[12px] text-[#FF465C]">
                              {helper("address")}
                            </p>
                          ) : null}
                        </motion.div>
                      </Grid2>
                    </>
                  )}
                </Grid2>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {isFinalStep && createError && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="mt-5 flex items-center justify-center"
            >
              <span className="text-[#FF465C] text-[13px] font-medium border border-solid w-fit bg-[#FF465C1a] rounded-full border-[#FF465C] px-3 py-1 text-center">
                {createError.message}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          layout
          transition={{ duration: 0.4, ease: EASE }}
          className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:items-center"
        >
          {step > 1 && (
            <div className="w-full sm:w-auto">
              <MaButton
                onClick={handleBack}
                disabled={createLoading}
                fullWidth
                startIcon={<IoArrowBack />}
                title="Back"
                sx={{
                  bgcolor: "transparent",
                  border: "1px solid #2E1834",
                  color: "#2E1834",
                  boxShadow: "none",
                  minWidth: { xs: "100%", sm: 140 },
                  borderRadius: "12px",
                }}
              />
            </div>
          )}
          <div className="w-full sm:flex-1">
            <MaButton
              onClick={validateAndProceed}
              variant="primary"
              fullWidth
              loading={createLoading}
              disabled={createLoading}
              title={ctaTitle}
              endIcon={
                !isFinalStep && !createLoading ? <IoIosArrowForward /> : null
              }
              sx={{
                minWidth: 0,
                bgcolor: "#2E1834",
                borderRadius: "12px",
              }}
            />
          </div>
        </motion.div>
      </div>
    </SellerAuth>
  );
};

export default CreateSellerStore;
